import { type ReactNode } from "react";
import { Help_1Icon, RemoveCircleIcon } from "vega-icons";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { type TicketSizeRange } from "@vega-api/generated/types/ticketSizeRange";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { PageHeader } from "@vega-core/components/Headers/PageHeader";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { type CoreCurrency, type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { formatMoney } from "@vega-core/utils/formatMoney";

import { AskQuestionModal } from "../components/AskQuestionModal";
import { IndicateInterestModal } from "../components/IndicateInterestModal";
import {
  type DeclineStage,
  RejectDealModal,
} from "../components/RejectDealModal";
import { SubmitTicketSizeModal } from "../components/SubmitTicketSizeModal";
import { useAcceptAllocation } from "../hooks/useAcceptAllocation";
import { useIndicateInterest } from "../hooks/useIndicateInterest";
import { useRejectDeal } from "../hooks/useRejectDeal";

export type CoInvestmentLayoutProps = {
  projectName?: string;
  tags?: string[];
  children: ReactNode;
  isLoading?: boolean;
  hasAccessToDataRoom?: boolean;
  hasIndicatedInterest: boolean;
  hasRejectedOffer: boolean;
  indicatedInterestAmount?: CoreMoney;
  commitmentAmount?: CoreMoney;
  allocationAmount?: CoreMoney;
  allocationAcceptedAt?: string;
  dealOpportunityId: string;
  dealId?: string;
  ticketSize?: TicketSizeRange;
  dealCurrency: CoreCurrency;
  canViewSensitiveOpportunityDetails?: boolean;
  canUpdateSensitiveOpportunityDetails?: boolean;
  isDealOpportunityClosed?: boolean;
  isDealClosed?: boolean;
  isNdaSigned?: boolean;
  targetAmount?: CoreMoney;
};

export const CoInvestmentLayout = ({
  projectName,
  tags,
  children,
  isLoading = false,
  hasAccessToDataRoom,
  hasIndicatedInterest,
  hasRejectedOffer,
  indicatedInterestAmount,
  commitmentAmount,
  allocationAmount,
  allocationAcceptedAt,
  dealOpportunityId,
  dealId,
  ticketSize,
  dealCurrency,
  canViewSensitiveOpportunityDetails = false,
  canUpdateSensitiveOpportunityDetails = false,
  isDealOpportunityClosed = false,
  isDealClosed = false,
  isNdaSigned = false,
  targetAmount,
}: CoInvestmentLayoutProps) => {
  // Either close concept makes the deal read-only for the LP: show the tag
  // and hide every mutation affordance
  const isDealReadOnly = isDealOpportunityClosed || isDealClosed;

  const { featureFlag } = useFeatureFlag();
  const isMessagingEnabled =
    featureFlag[FeatureFlag.COINVEST_MESSAGING_VD_28584];

  const getDeclineStage = (): DeclineStage => {
    if (allocationAmount) return "post-allocation";

    if (isNdaSigned) return "post-nda";

    return "pre-nda";
  };

  const declineStage = getDeclineStage();

  const { handleRejectDeal, isRejectingDeal, rejectModal } = useRejectDeal({
    dealOpportunityId,
    dealId,
    declineStage,
  });

  const {
    modal: indicateInterestModal,
    handleIndicateInterest,
    isSubmitting,
  } = useIndicateInterest({
    dealOpportunityId,
    initialMoney: indicatedInterestAmount,
  });

  const { handleAcceptAllocation, isAcceptingAllocation } = useAcceptAllocation(
    {
      dealOpportunityId,
    },
  );

  const {
    isOpen: isSubmitTicketSizeModalOpen,
    setTrue: openSubmitTicketSizeModal,
    setFalse: closeSubmitTicketSizeModal,
  } = useBoolean(false);

  const {
    isOpen: isAskQuestionModalOpen,
    setTrue: openAskQuestionModal,
    setFalse: closeAskQuestionModal,
  } = useBoolean(false);

  const canActOnOpportunity =
    !hasRejectedOffer &&
    canUpdateSensitiveOpportunityDetails &&
    !isDealReadOnly &&
    !allocationAcceptedAt;

  const handleRejectDealSubmit = (values: {
    reason: string;
    comment?: string;
  }) => {
    handleRejectDeal(values);
  };

  return (
    <>
      <div className="p-4 lg:p-8">
        <div className="mb-6 lg:mb-8 md:container">
          <div className="flex flex-wrap items-center md:flex-row md:justify-between gap-4">
            <PageHeader
              action={
                canActOnOpportunity ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-4">
                    {isMessagingEnabled ? (
                      <ThemedButton
                        dataTestId="coinvest-ask-question-button"
                        icon={<Help_1Icon />}
                        isLoading={isLoading}
                        onClick={openAskQuestionModal}
                        variant="secondary"
                      >
                        <span className="hidden md:block">
                          {m.coInvestments_messages_askQuestion_button()}
                        </span>
                      </ThemedButton>
                    ) : null}
                    <ThemedButton
                      dataTestId="reject-deal-button"
                      isLoading={isLoading}
                      onClick={rejectModal.onOpen}
                      variant="destructive"
                    >
                      <span className="flex gap-2 items-center">
                        <RemoveCircleIcon className="hidden md:inline" />
                        {declineStage === "post-allocation"
                          ? m.coInvestments_declineAllocation_title()
                          : m.coInvestments_declineDeal_title()}
                      </span>
                    </ThemedButton>
                    {isNdaSigned && !allocationAmount ? (
                      <>
                        <ThemedButton
                          ariaLabel={m.coInvestments_submitTicketSize_button()}
                          dataTestId="submit-ticket-size-button"
                          isLoading={isLoading}
                          onClick={openSubmitTicketSizeModal}
                          variant="secondary"
                        >
                          {m.coInvestments_submitTicketSize_button()}
                        </ThemedButton>
                        <ThemedButton
                          ariaLabel={m.coInvestments_indicateInterest_title()}
                          dataTestId="indicate-interest-button"
                          isLoading={isLoading}
                          onClick={indicateInterestModal.onOpen}
                          variant="primary"
                        >
                          {m.coInvestments_indicateInterest_title()}
                        </ThemedButton>
                      </>
                    ) : null}
                  </div>
                ) : null
              }
              className="p-0"
              isLoading={isLoading}
              title={
                <div className="flex flex-wrap items-center gap-4">
                  <span>{projectName}</span>
                  {isDealReadOnly ? (
                    <Tag
                      dataTestId="deal-closed-tag"
                      variant={TagVariants.Data1}
                    >
                      {m.coInvestments_dealClosed_tag()}
                    </Tag>
                  ) : null}
                </div>
              }
            />
          </div>
          {tags && !isLoading ? (
            <div className="flex gap-2 mt-4 flex-wrap">
              {tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </div>
          ) : (
            <div className="skeleton-box h-6 w-20 rounded-full" />
          )}
        </div>

        {canViewSensitiveOpportunityDetails &&
        commitmentAmount &&
        allocationAcceptedAt ? (
          <div className="mb-6 md:container">
            <Notice
              content={
                <div className="flex flex-col">
                  <span className="body-base-medium">
                    {m.coInvestments_allocation_accepted_title()}
                  </span>
                  <span className="body-lg-semibold">
                    {formatMoney(commitmentAmount)}
                  </span>
                </div>
              }
              type={NoticeType.SUCCESS}
            />
          </div>
        ) : null}

        {canViewSensitiveOpportunityDetails &&
        allocationAmount &&
        !allocationAcceptedAt &&
        !hasRejectedOffer ? (
          <div className="mb-6 md:container">
            <Notice
              action={
                canUpdateSensitiveOpportunityDetails && !isDealReadOnly ? (
                  <ThemedButton
                    ariaLabel={m.coInvestments_allocation_accept_button()}
                    dataTestId="accept-allocation-button"
                    isLoading={isLoading || isAcceptingAllocation}
                    onClick={handleAcceptAllocation}
                    variant="secondary"
                  >
                    {m.coInvestments_allocation_accept_button()}
                  </ThemedButton>
                ) : null
              }
              content={
                <div className="flex flex-col">
                  <span className="body-base-medium">
                    {m.coInvestments_allocation_receivedTitle()}
                  </span>
                  <span className="body-lg-semibold">
                    {formatMoney(allocationAmount)}
                  </span>
                </div>
              }
              type={NoticeType.PENDING}
            />
          </div>
        ) : null}

        {canViewSensitiveOpportunityDetails &&
        hasIndicatedInterest &&
        indicatedInterestAmount &&
        !allocationAmount ? (
          <div className="mb-6 md:container">
            <Notice
              content={m.coInvestments_indicateInterest_successMessage({
                amount: formatMoney(indicatedInterestAmount),
              })}
              details={m.coInvestments_indicateInterest_successDetails()}
              expandable
              expandedByDefault
              type={NoticeType.SUCCESS}
            />
          </div>
        ) : null}

        {hasRejectedOffer ? (
          <div className="mb-6 md:container">
            <Notice
              content={m.coInvestments_declineDeal_bannerMessage()}
              details={m.coInvestments_declineDeal_bannerDetails()}
              expandable
              expandedByDefault
              type={NoticeType.ERROR}
            />
          </div>
        ) : null}
        {hasAccessToDataRoom === false && !hasRejectedOffer && (
          <Notice
            className="mb-6"
            content={m.coInvestments_acceptNda_notice()}
            type={NoticeType.BRAND}
          />
        )}

        {children}
      </div>

      <RejectDealModal
        declineStage={declineStage}
        isOpen={rejectModal.isOpen}
        isSubmitting={isRejectingDeal}
        onClose={rejectModal.onClose}
        onSubmit={handleRejectDealSubmit}
      />

      <IndicateInterestModal
        dealCurrency={dealCurrency}
        initialMoney={indicatedInterestAmount}
        isOpen={indicateInterestModal.isOpen}
        isSubmitting={isSubmitting}
        maxAmount={targetAmount}
        onClose={indicateInterestModal.onClose}
        onSubmit={handleIndicateInterest}
      />

      <SubmitTicketSizeModal
        dealCurrency={dealCurrency}
        dealOpportunityId={dealOpportunityId}
        initialValues={ticketSize}
        isOpen={isSubmitTicketSizeModalOpen}
        maxAmount={targetAmount}
        onClose={closeSubmitTicketSizeModal}
      />

      {isAskQuestionModalOpen && dealId ? (
        <AskQuestionModal
          dealId={dealId}
          dealName={projectName ?? ""}
          dealOpportunityId={dealOpportunityId}
          onClose={closeAskQuestionModal}
        />
      ) : null}
    </>
  );
};
